import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { updatePreferences } from 'lib/context/TableContext/helper';
import { useTableContext } from 'lib/context/TableContext/TableContext';

import Icon from '../../../Icon/Icon';
import Button from '../../../Button/Button';
import Divider from '../../../Divider/Divider';
import Typography from '../../../Typography/Typography';
import DisplayButton from '../../../DisplayButton/DisplayButton';

import GroupOptions from './GroupOptions';
import OrderOptions from './OrderOptions';
import SubGroupOptions from './SubGroupOptions';
import RowsPerPageOptions from './RowsPerPageOptions';

const DisplayButtonAGTable = ({ btnClassName = '', color = 'neutral', textClassName = '' }) => {
  const { limit, setLimit, setColDefs, colDefs, defaultColumns, name, setPage, gridApi, gridRef } =
    useTableContext();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const onDragEnd = async (items) => {
    setColDefs(items);
    await updatePreferences({ navigate, cols: items, limit, queryClient, name });
  };

  const togglePinned = async (direction, colIndex) => {
    const newColDefs = colDefs.map((col, index) => {
      if (index === colIndex) {
        if (col.pinned === direction) {
          col.pinned = false;
        } else {
          col.pinned = direction;
        }
      }
      return col;
    });
    setColDefs(newColDefs);
    await updatePreferences({ navigate, cols: newColDefs, limit, queryClient, name });
    queryClient.invalidateQueries(['table-limit']);
  };

  const handleChangeLimit = async (item) => {
    setLimit(item);
    setPage(1);
    await updatePreferences({ navigate, cols: colDefs, limit: item, queryClient, name });
    queryClient.invalidateQueries(['table-limit']);
  };

  const resetSortingAndGrouping = async () => {
    const newColDefs = defaultColumns.map((c) => ({
      ...c,
      sort: null,
      sortIndex: null,
      rowGroup: false,
      rowGroupIndex: null,
      pinned: c.pinned ?? null,
      hide: c.hide ?? false
    }));

    if (gridApi?.applyColumnState) {
      gridApi.applyColumnState({
        state: newColDefs
          .filter((c) => c.colId || c.field)
          .map((c) => ({
            colId: c.colId || c.field,
            sort: null,
            sortIndex: null,
            rowGroup: false,
            rowGroupIndex: null,
            pinned: c.pinned,
            hide: c.hide
          })),
        defaultState: {
          sort: null,
          sortIndex: null,
          rowGroup: false,
          rowGroupIndex: null,
          pinned: null,
          hide: false
        },
        applyOrder: true
      });
    }

    setColDefs(newColDefs);
    await updatePreferences({ navigate, cols: newColDefs, limit, queryClient, name });
  };

  const autosizeAllColumns = () => {
    const columnApi =
      gridApi?.getColumnApi?.() ||
      (typeof gridRef?.current?.columnApi !== 'undefined' ? gridRef.current.columnApi : null);

    if (columnApi?.autoSizeAllColumns) {
      columnApi.autoSizeAllColumns();
      return;
    }

    if (gridApi?.autoSizeAllColumns) {
      gridApi.autoSizeAllColumns();
      return;
    }

    if (gridApi?.autoSizeColumns && gridApi?.getDisplayedCenterColumns) {
      const ids = gridApi.getDisplayedCenterColumns().map((c) => c.getColId());
      gridApi.autoSizeColumns(ids);
    }
  };

  return (
    <DisplayButton
      columns={colDefs}
      onPinChange={togglePinned}
      onDragEnd={onDragEnd}
      onColumnChange={setColDefs}
      btnClassName={btnClassName}
      color={color}
      textClassName={textClassName}
      disabledActions={['left']}
      disabledItems={['actions']}>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1 py-2">
          <div className="flex items-center justify-between gap-2">
            <Typography variant="label" size="small" weight="semibold">
              Display options
            </Typography>
            <Button
              size="xs"
              text="Reset to default"
              variant="link"
              onClick={resetSortingAndGrouping}
            />
          </div>
          <GroupOptions />
          <SubGroupOptions />
          <OrderOptions />
          <RowsPerPageOptions onChange={handleChangeLimit} />
        </div>
        <Divider />
        <div className="flex gap-1 px-4 py-2 hover:bg-neutral-100" onClick={autosizeAllColumns}>
          <Icon icon="resize-horizontally-mono" color={color} shade={600} />
          <Typography variant="label" size="medium">
            Autosize all columns
          </Typography>
        </div>
      </div>
    </DisplayButton>
  );
};

export default DisplayButtonAGTable;
